import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission, constructQuery, EventEmitter } from '@repo/shared/helpers';
import { guardedClient as request } from '@repo/shared/helpers';
import { Spinner } from '@repo/components';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getProject } from 'src/api/admin/projects';
import { getEventsWithHierarchy } from 'src/api/admin/events';
import { getEventTypes } from 'src/api/admin/types';
import { getAllTags } from 'src/api/admin/tags';
import SubcontractingTab from 'src/parts/tabs/SubcontractingTab';
import { useConfig } from 'src/hooks/useConfig';
interface ProjectEventsSearch {
	page?: number;
	sort?: string;
}

export const Route = createFileRoute('/_admin/projects/$projectId/events')({
	validateSearch: (search: Record<string, unknown>): ProjectEventsSearch => ({
		page: search.page as number | undefined,
		sort: search.sort as string | undefined,
	}),
	component: ProjectEventsPage,
});

function ProjectEventsPage() {
	const { projectId } = Route.useParams();
	const { page, sort } = Route.useSearch();
	const queryClient = useQueryClient();

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const { config } = useConfig();
	const { featureFlags } = config || {};
	const usesTags = featureFlags?.USES_TAGS || false;
	const query = constructQuery({
		page,
		sort,
		projectIds: projectId,
	});

	const { data: project, isLoading: projectLoading } = useQuery({
		queryKey: ['project', projectId],
		queryFn: () => getProject(projectId),
	});

	const { data: eventsData, isLoading: eventsLoading } = useQuery({
		queryKey: ['project-events', projectId, query],
		queryFn: () => getEventsWithHierarchy(query),
		enabled: !!project,
	});

	const { data: typesData } = useQuery({
		queryKey: ['event-types'],
		queryFn: () => getEventTypes(),
	});

	const { data: tagsData } = useQuery({
		queryKey: ['tags'],
		queryFn: () => getAllTags(),
	});

	const revalidateEvents = () => {
		queryClient.invalidateQueries({ queryKey: ['project-events', projectId] });
	};

	const addEvent = async (data: unknown) => {
		await request
			.post('/api/events', data)
			.then(() => {
				EventEmitter.emit('toast', {
					type: 'success',
					message: 'Event created',
				});
			})
			.catch((error: { message?: string; body?: { message?: string } }) => {
				const errorMessage =
					error?.message || error?.body?.message || 'Failed to create Event. Did you enter all data correctly?';
				EventEmitter.emit('toast', {
					type: 'error',
					message: errorMessage,
				});
				throw error;
			});
	};

	const isLoading = authLoading || projectLoading || eventsLoading;

	if (isLoading || !authenticatedUser || !project) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	const types = typesData?.data || [];
	const tags = tagsData || [];

	return (
		<SubcontractingTab
			authenticatedUser={authenticatedUser}
			events={eventsData}
			addEvent={addEvent}
			permissions={{
				view: verifyPermission(PERMISSIONS.adminEventsView),
				create: verifyPermission(PERMISSIONS.adminEventsCreate),
				update: verifyPermission(PERMISSIONS.adminEventsEdit),
				archive: verifyPermission(PERMISSIONS.adminEventsArchive),
			}}
			onReload={revalidateEvents}
			types={types}
			project={project}
			tags={tags}
			usesTags={usesTags}
			skipOwnerEmailDomainCheck
		/>
	);
}
